/**
 * liveAudiencePresence — a tiny cross-tab presence ledger for the community LIVE
 * audience, backed by `localStorage`.
 *
 * Web-specific problem (no Flutter analog — one live screen at a time on mobile):
 * the audience watcher count is a Firestore counter bumped +1 on channel-connected
 * and −1 on dispose (`useLiveStreaming`). Nothing dedupes a single user across
 * browser tabs, so the same subscriber opening the community wall in N tabs and
 * tapping "Join Live" in each would over-report the broadcaster's audience by
 * (N − 1).
 *
 * `localStorage` is shared SYNCHRONOUSLY across all same-origin tabs, so the
 * "am I already watching this session?" check is a pure local read — it satisfies
 * the user's two constraints for the fix: NO per-join Firestore query and NO extra
 * backend read (the Agora token mint). The community page gates the "Join Live"
 * tap on `isWatchingLive`, so only ONE tab per user ever reaches the live page and
 * the existing +1 / −1 watcher logic stays correct with no reference counting.
 *
 * Keyed by `userId` so a user switch on the same browser cannot inherit a stale
 * flag. This is a plain presentation utility — no React hooks
 * (`conventions.md` § New responsibility → new owner file).
 */
import { appLogger } from '@/domain/services/analytics/appLogger';

/** The single `localStorage` key holding the `{ [userId]: liveMessageId[] }` map. */
const STORAGE_KEY = 'expause.live.audiencePresence';

/** `{ [userId]: string[] }` — the live-message ids each user is currently watching. */
type PresenceMap = Record<string, string[]>;

/** SSR / private-mode defensive: `localStorage` is absent outside the browser. */
function getStorage(): Storage | null {
  try {
    if (typeof localStorage === 'undefined') {
      return null;
    }
    return localStorage;
  } catch {
    // Some browsers throw on `localStorage` access under strict privacy settings.
    return null;
  }
}

/** Read + parse the map, tolerating an absent / corrupt value (→ empty map). */
function readMap(storage: Storage): PresenceMap {
  const raw = storage.getItem(STORAGE_KEY);
  if (raw === null) {
    return {};
  }
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (parsed === null || typeof parsed !== 'object') {
      return {};
    }
    return parsed as PresenceMap;
  } catch {
    // Corrupt value → treat as "not watching" (fail-open re-join over a false block).
    return {};
  }
}

/** Serialize the map back, logging (never throwing) on a write failure (quota, etc.). */
function writeMap(storage: Storage, map: PresenceMap): void {
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch (error) {
    appLogger.error(
      'failed to persist live audience presence',
      'liveAudiencePresence.writeMap',
      { error: error instanceof Error ? error : new Error(String(error)) },
    );
  }
}

/**
 * True when THIS user already has a tab watching `liveStreamingMessageId` (any
 * same-origin tab). A false / corrupt / absent value reads as "not watching" so a
 * legitimate re-join is never wrongly blocked.
 */
export function isWatchingLive(
  userId: string,
  liveStreamingMessageId: string,
): boolean {
  const storage = getStorage();
  if (storage === null) {
    return false;
  }
  const ids = readMap(storage)[userId];
  return Array.isArray(ids) && ids.includes(liveStreamingMessageId);
}

/**
 * Record that this user is now watching `liveStreamingMessageId` in the current
 * tab. Idempotent — a second call for an already-present id is a no-op.
 */
export function markWatchingLive(
  userId: string,
  liveStreamingMessageId: string,
): void {
  const storage = getStorage();
  if (storage === null) {
    return;
  }
  const map = readMap(storage);
  const ids = Array.isArray(map[userId]) ? map[userId] : [];
  if (ids.includes(liveStreamingMessageId)) {
    return;
  }
  map[userId] = [...ids, liveStreamingMessageId];
  writeMap(storage, map);
}

/**
 * Release this user's presence for `liveStreamingMessageId` (on leave / session
 * end), so a later re-join is allowed. Drops the user's entry entirely once empty
 * to keep the map from accreting stale keys.
 */
export function clearWatchingLive(
  userId: string,
  liveStreamingMessageId: string,
): void {
  const storage = getStorage();
  if (storage === null) {
    return;
  }
  const map = readMap(storage);
  const ids = map[userId];
  if (!Array.isArray(ids) || !ids.includes(liveStreamingMessageId)) {
    return;
  }
  const remaining = ids.filter((id) => id !== liveStreamingMessageId);
  if (remaining.length === 0) {
    delete map[userId];
  } else {
    map[userId] = remaining;
  }
  writeMap(storage, map);
}
